import { LOCALE_ID, Inject, Component, OnInit, ChangeDetectorRef, Output, EventEmitter } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { FormGroup, FormBuilder, Validators, FormControl } from '@angular/forms';
import { Order, LogElement } from '@models/order';
import { Product, ProductHistory } from '@models/product';
import { VendorOrder } from '@models/vendor-order';
import { OrderService } from '@services/order.service';
import { ProductService } from '@services/product.service';
import { StoreService } from '@services/store.service';
import { AuthService } from '@services/auth.service';
import { VendorOrderService } from '@services/vendor-order.service';
import {
  combineLatest,
  concatMap,
  forkJoin,
  map,
  Observable,
} from 'rxjs';
import { OrdersBaseComponent } from '@components/admin/managed-board/base/orders-base/orders-base.component';
import { NotificationService } from '@services/notification.service';
import { getPurchaseTaxFromHistory } from '@classes/product-tax';
import ObjectID from 'bson-objectid';
import { MatDialog } from '@angular/material/dialog';
import { OrderEditDialogComponent } from '@components/admin/patient/order-edit-dialog/order-edit-dialog.component';

@Component({
  selector: 'app-returns-orders',
  templateUrl: './returns-orders.component.html',
  styleUrls: [
    '../../managed-board.component.css',
    './returns-orders.component.css',
  ],
})
export class ReturnsOrdersComponent
  extends OrdersBaseComponent
  implements OnInit
{
  @Output() refreshOrders = new EventEmitter<boolean>();
  displayedColumns = [
    'select',
    'image',
    'model',
    'color',
    'size',
    'type',
    'cost',
    'retail',
    'store',
    'view',
    'action',
    'log',
  ];
  authorizedRoles: string[] = ['MANAGER', 'OWNER/ADMIN', 'OPTICIAN/INVENTORY'];

  selectedOrderIds: string[] = [];

  get allSelectedIds() {
    return this.selectedOrderIds;
  }

  get framesSelected(): number {
    return this.selectedOrderIds.length;
  }

  constructor(
    @Inject(LOCALE_ID) public locale: string,
    public auth: AuthService,
    public orderService: OrderService,
    public vendorOrderService: VendorOrderService,
    public productService: ProductService,
    public storeService: StoreService,
    public cdRef: ChangeDetectorRef,
    public fb: FormBuilder,
    public route: ActivatedRoute,
    public notificationService: NotificationService,
    private dialog: MatDialog
  ) {
    super(
      locale,
      auth,
      orderService,
      vendorOrderService,
      productService,
      storeService,
      cdRef,
      fb,
      route,
      notificationService
    );
  }

  ngOnInit() {
    super.ngOnInit();
    this.selection.changed.subscribe((change) => {
      this.selectedOrderIds = this.selection.selected.map((s) => s.value._id);
    });
  }

  ngAfterViewInit() {
    super.ngAfterViewInit();
    this.orderForm.addControl(
      'raNumber',
      new FormControl('', Validators.required)
    );
    this.orderForm.addControl(
      'date',
      new FormControl(new Date(), Validators.required)
    );
    this.orderForm.addControl(
      'shippingMethod',
      new FormControl('', Validators.required)
    );
    this.orderForm.addControl(
      'trackingNumber',
      new FormControl('', Validators.required)
    );
    this.orderForm.addControl(
      'proposedCredit',
      new FormControl('', Validators.required)
    );
    this.orderForm.addControl(
      'initials',
      new FormControl('', Validators.required)
    );
    this.cdRef.detectChanges();
  }


  isAllSelected() {
    const numSelected = this.selection.selected.length;
    const numRows = this.dataSource.data.length;
    return numSelected === numRows;
  }

  masterToggle() {
    this.isAllSelected()
      ? this.selection.clear()
      : this.dataSource.data.forEach((row) => this.selection.select(row));
    this.loadProductHistoryForSelected();
    this.calculateTotal();
    this.cdRef.detectChanges();
  }

  toggleSelect(order) {
    this.selection.toggle(order);
    this.calculateTotal();
    this.cdRef.detectChanges();
    this.loadProductHistoryForSelected();
  }

  private loadProductHistoryForSelected() {
    this.selection.selected.forEach((orderGroup) => {
      const order = orderGroup.getRawValue();
      const existingTax = orderGroup.get('products.0.tax')?.value;
      if (existingTax != null && existingTax !== '' && +existingTax > 0) {
        return;
      }

      const productId = order.products[0]?.product?._id;
      if (!productId) {
        return;
      }

      this.vendorOrderService
        .getProductHistory(productId)
        .subscribe((productHistory: ProductHistory[]) => {
          const tax = getPurchaseTaxFromHistory(productHistory ?? []);
          orderGroup.get('products.0.tax').setValue(tax);
          this.calculateTotal();
        });
    });
  }

  calculateTotal() {
    let total = 0;
    let tax = 0;
    
    this.selection.selected.forEach((order) => {
      total += order.value.products[0].cost;
      tax += order.value.products[0].tax;
    });
    
    if (!isNaN(tax)) {
      total += tax;
    }
    
    this.orderForm.get('proposedCredit').setValue(total);
  }

  checkboxLabel(row?: FormGroup, index?): string {
    if (!row) {
      return `${this.isAllSelected() ? 'select' : 'deselect'} all`;
    }
    return `${this.selection.isSelected(row) ? 'deselect' : 'select'} row ${
      index + 1
    }`;
  }

  clickOrder($event, order: FormGroup, index) {
    if (!this.fieldOpen) {
      if (this.orderIndex && this.index) {
        order.controls.notify.disable();
      }
      order.controls.notify.enable();
      this.orderId = order.value._id;
      this.index = index;
      this.fieldOpen = true;
      this.cdRef.detectChanges();
    }
  }

  openOrderEdit($event: Event, order: FormGroup): void {
    $event.preventDefault();
    $event.stopPropagation();
    const patientId =
      order.value?.patient?._id || order.value?.patient || null;
    const orderId = order.value?._id;
    if (!patientId || !orderId) {
      return;
    }

    const dialogRef = this.dialog.open(OrderEditDialogComponent, {
      width: '96vw',
      maxWidth: '96vw',
      height: '92vh',
      maxHeight: '92vh',
      autoFocus: false,
      panelClass: 'order-edit-dialog-panel',
      data: { patientId, orderId },
    });

    dialogRef.afterClosed().subscribe((saved) => {
      if (saved) {
        this.refreshOrders.emit();
      }
    });
  }

  private readonly ORDER_STATUS_RETURNED = 'RETURNED';
  private readonly PRODUCT_STATUS_RETURNED = 'returned';
  private readonly PRODUCT_STATUS_STOCK = 'stock';

  saveIncomingOrder(incomingBoolean) {
    this.fieldOpen = true;

    const logItem = this.createLogItem();
    const vendorOrder = this.createVendorOrder(logItem);

    const orderUpdates = this.selection.selected.map((selectedOrder, index) => {
      const order = selectedOrder.getRawValue();
      this.prepareOrderForReturn(order, logItem, vendorOrder);

      return this.processOrderReturn(order, vendorOrder, index === this.selection.selected.length - 1);
    });

    forkJoin(orderUpdates).subscribe(() => {
      this.vendorOrderService.addVendorOrder(vendorOrder).subscribe(() => {
        this.refreshOrders.emit(true);
      });
    });
  }

  private createLogItem(): LogElement {
    const logItem = new LogElement();
    logItem.initials = this.orderForm.value.initials;
    logItem.user = this.auth.currentUser._id;
    logItem.item = 'AWAITING CREDIT';
    logItem.notes = '';
    logItem.date = Date.now().toString();
    return logItem;
  }

  private createVendorOrder(logItem: LogElement): VendorOrder {
    let total = this.orderForm.get('proposedCredit').value;
    if (typeof total === 'string') {
      total = total.replace(/,/g, '');
    }
    total = -1 * parseFloat(total);

    let tax = 0;
    this.selection.selected.forEach((orderGroup) => {
      tax += +(orderGroup.get('products.0.tax')?.value ?? 0);
    });

    const vendorOrder = new VendorOrder();
    vendorOrder.date = this.orderForm.value.date;
    vendorOrder.type = 'return';
    vendorOrder.raNumber = this.orderForm.value.raNumber;
    vendorOrder.shippingMethod = this.orderForm.value.shippingMethod;
    vendorOrder.trackingNumber = this.orderForm.value.trackingNumber;
    vendorOrder.tax = tax;
    vendorOrder.total = total;
    vendorOrder.proposedCredit = parseFloat(this.orderForm.value.proposedCredit);
    vendorOrder.vendor = this.vendorOrders.vendor;
    vendorOrder.user = this.auth.currentUser._id;
    vendorOrder.store = this.auth.getStore();
    vendorOrder.status = this.ORDER_STATUS_RETURNED;
    vendorOrder.log.unshift(logItem);
    return vendorOrder;
  }

  private prepareOrderForReturn(order: any, logItem: LogElement, vendorOrder: VendorOrder): void {
    order.status = this.ORDER_STATUS_RETURNED;
    order.log.unshift(logItem);

    order.products[0].store = order.products[0].store._id;
    order.products[0].product.cost = order.products[0].cost;
    order.products[0].product.retail = order.products[0].retail;
    order.products[0].product.pricingFormula = order.products[0].pricingFormula;
    order.products[0].product.type = order.products[0].type;
    
    vendorOrder.products.unshift(order.products[0]);
    vendorOrder.orders.unshift(order._id);
  }

  private processOrderReturn(order: any, vendorOrder: VendorOrder, isLast: boolean): Observable<any> {
    return this.productService
      .incrementProductInventoryQuantity(order.products[0].product, -1)
      .pipe(
        concatMap((incrementProduct) => {
          if (!order.products[0].product._id) {
            order.products[0].product._id = incrementProduct.product._id;
          }
          
          // If quantity is 1 or greater after return, set status to 'stock', otherwise 'returned'
          const newQuantity = incrementProduct.product.quantity;
          const status = newQuantity >= 1 ? this.PRODUCT_STATUS_STOCK : this.PRODUCT_STATUS_RETURNED;
          
          return this.productService.setStatus(
            order.products[0].product._id,
            status
          );
        }),
        concatMap(() => this.orderService.updateOrderStatusAndLog(order))
      );
  }

  removeFramesFromVendorOrder() {
    const updates = this.selectedOrderIds.map((orderId) => {
      const logItem = this.createReturnToInventoryLogItem();
      return this.updateOrders(orderId, logItem);
    });

    combineLatest(updates).subscribe(() => {
      this.refreshOrders.emit(true);
    });
  }

  private createReturnToInventoryLogItem(): LogElement {
    return {
      _id: new ObjectID().toHexString(),
      initials: this.auth.currentUser.name
        .split(' ')
        .map((item) => item[0].toUpperCase())
        .join(''),
      user: this.auth.currentUser._id,
      item: 'RETURN FRAME TO INVENTORY',
      date: new Date().toISOString(),
    };
  }

  private readonly ORDER_STATUS_CANCELLED = 'CANCELLED';

  getOrderUpdates(orderId: string, logItem: LogElement) {
    return this.orderService.getOrderById(orderId).pipe(
      map((res) => {
        const productsUpdates = res.products.map((product) => {
          const dateUpdated = new Date().toISOString();
          const quantity = product.quantity || 1;
          return this.updateProductStatus(product.product._id, {
            status: null,
            dateUpdated,
            quantity,
          });
        });

        const updates: Partial<Order> = {
          products: [],
          todo: null,
          log: [logItem],
          status: this.ORDER_STATUS_CANCELLED,
        };

        return {
          order: updates,
          productsUpdates,
        };
      })
    );
  }

  updateOrders(orderId: string, logItem: LogElement) {
    return this.getOrderUpdates(orderId, logItem).pipe(
      concatMap(({ order, productsUpdates }) =>
        this.orderService
          .updateOrderById(orderId, order)
          .pipe(concatMap(() => forkJoin(productsUpdates)))
      )
    );
  }

  updateProductStatus(
    productId: string,
    productUpdates: Partial<Product>
  ): Observable<any> {
    return this.productService.getProduct(productId).pipe(
      map((res) => ({ ...res, ...productUpdates })),
      concatMap((mergedProduct: Product) =>
        this.productService.updateProduct(productId, mergedProduct)
      )
    );
  }
}
